import {
    Box,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";
import { LLMClassifierVersionComparison } from "../../Components/VersionComparison/LLMClassifierVersionComparison";
import { isLLMClassifierVersionPublished } from "../../utils/llmClassifierVersionDisplay";
import { observer } from "mobx-react";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { useVersionSwitch } from "../../Components/VersionSwitch";
import { LLMClassifierApprovalWorkflow } from "../../Components/Approvals/LLMClassifierApprovalWorkflow";
import { useReadToBottom } from "../../utils/useReadToBottom";
import { VersionComparisonDialog } from "../../Components/VersionComparison/VersionComparisonDialog";
import { LLMClassifierVersionHistoryItem } from "./LLMClassifierVersionHistoryItem";

/** Every label is `vYYYYMMDD-HHmm`, so compact can size itself to exactly that. */
const VERSION_LABEL_LENGTH = 14;

type ListDensity = "comfortable" | "compact";

const DENSITY_STORAGE_KEY = "llmClassifierVersionListDensity";

const readDensityPreference = (): ListDensity =>
    localStorage.getItem(DENSITY_STORAGE_KEY) === "compact"
        ? "compact"
        : "comfortable";

export const LLMClassifierVersionHistory = observer(function () {
    const store = useStore(LLMClassifierBuilderStore);
    const { selectVersion } = useVersionSwitch();
    const isLoading = store.getTaskLoading(
        LLMClassifierBuilderStore.Tasks.LoadLLMClassifierVersions,
    );

    const versions = store.llmClassifierVersions;
    const loadedVersionId = store.classifier.implementation.id;
    const loadedVersion = versions.find(
        (version) => version.id === loadedVersionId,
    );
    const loadedVersionRef = useRef<HTMLDivElement | null>(null);
    const [density, setDensity] = useState<ListDensity>(readDensityPreference);
    const isCompact = density === "compact";

    // Callback refs: each pane's gate has to start the moment its node exists,
    // and the dialog's only exists once it is open.
    const [inlineScroller, setInlineScroller] = useState<HTMLElement | null>(
        null,
    );
    const [fullScreenScroller, setFullScreenScroller] =
        useState<HTMLElement | null>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);

    const isDiffMeasurable = !isLoading && versions.length > 0;
    // What was read is a pair, not a version: the pickers can swap either side
    // for a longer diff without the loaded version changing, and that diff has
    // not been read.
    const comparison = store.comparisonVersions;
    const readGateKey = `${loadedVersionId}:${comparison.before?.id}:${comparison.after?.id}`;
    const hasReadInline = useReadToBottom(
        inlineScroller,
        readGateKey,
        isDiffMeasurable,
    );
    // Held by this component rather than the dialog, so reading it through once
    // still counts after the dialog closes.
    const hasReadFullScreen = useReadToBottom(
        fullScreenScroller,
        readGateKey,
        isDiffMeasurable && isFullScreen,
    );
    const hasReadDiff = hasReadInline || hasReadFullScreen;

    // Back to the top for a new pair, in both panes. A retained scroll position
    // can already sit at the end of the diff that replaced it, which would open
    // the gate on changes nobody has looked at. Separate effects so opening the
    // dialog does not rewind the pane behind it.
    useEffect(() => {
        if (inlineScroller) inlineScroller.scrollTop = 0;
    }, [readGateKey, inlineScroller]);
    useEffect(() => {
        if (fullScreenScroller) fullScreenScroller.scrollTop = 0;
    }, [readGateKey, fullScreenScroller]);

    const approvalWorkflow = loadedVersion && (
        <LLMClassifierApprovalWorkflow
            key={loadedVersion.id}
            version={loadedVersion}
            isCondensed={!hasReadDiff}
            actionDisabledReason={
                hasReadDiff
                    ? undefined
                    : "Scroll to the end of the changes to enable"
            }
        />
    );

    // Stable, or the comparison's memo breaks and every diff block re-renders
    // and re-diffs each time this component does -- including on every toggle.
    const openFullScreen = useCallback(() => setIsFullScreen(true), []);

    const densityLabel = isCompact
        ? "Expand version history"
        : "Collapse version history";

    const toggleDensity = () => {
        const next: ListDensity = isCompact ? "comfortable" : "compact";
        setDensity(next);
        localStorage.setItem(DENSITY_STORAGE_KEY, next);
    };

    // Density changes every row's height, so re-center rather than keep the scroll position.
    useLayoutEffect(() => {
        if (isLoading) return;
        loadedVersionRef.current?.scrollIntoView({ block: "center" });
    }, [loadedVersionId, isLoading, density]);

    useEffect(() => {
        store.loadLLMClassifierVersions();
    }, [store, store.classifier.id, store.classifier.implementation.versionNumber]);

    return (
        <Stack
            direction="row"
            gap={2}
            width="100%"
            height="100%"
            minHeight={0}
            alignItems="stretch"
        >
            <Paper
                variant="outlined"
                // Row heights are only final once the width animation is.
                onTransitionEnd={(event) => {
                    if (
                        event.target === event.currentTarget &&
                        event.propertyName === "width"
                    )
                        loadedVersionRef.current?.scrollIntoView({
                            block: "center",
                        });
                }}
                sx={(theme) => ({
                    // Compact is only as wide as a version label plus its
                    // padding, handing the rest back to the diffs.
                    width: isCompact
                        ? `calc(${VERSION_LABEL_LENGTH}ch + ${theme.spacing(
                              7,
                          )})`
                        : { xs: 320, xl: 400 },
                    flexShrink: 0,
                    overflow: "hidden",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    transition: theme.transitions.create("width", {
                        duration: theme.transitions.duration.shortest,
                    }),
                })}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    px={2}
                    py={1.5}
                    borderBottom={1}
                    borderColor="gray.200"
                >
                    <Typography
                        fontSize={isCompact ? "13px" : "15px"}
                        fontWeight={600}
                        noWrap
                    >
                        {isCompact ? "Versions" : "Version history"}
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography color="gray.500" variant="body2" noWrap>
                            {isCompact
                                ? versions.length
                                : `${versions.length} versions`}
                        </Typography>
                        <Tooltip title={densityLabel}>
                            {/* Points into the panel to collapse it, back out to reopen it. */}
                            <IconButton
                                size="small"
                                aria-label={densityLabel}
                                aria-pressed={isCompact}
                                onClick={toggleDensity}
                                sx={{ color: "gray.600", mr: -0.75 }}
                            >
                                {isCompact ? (
                                    <ChevronRightRounded fontSize="small" />
                                ) : (
                                    <ChevronLeftRounded fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {!isCompact && (
                    <Box
                        px={2}
                        py={1}
                        borderBottom={1}
                        borderColor="gray.200"
                        bgcolor="gray.50"
                    >
                        <Typography color="gray.600" variant="body2">
                            Click a version title to open it in the Builder.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ overflowY: "auto", flex: 1 }}>
                    {isLoading && (
                        <Stack gap={1} p={2}>
                            {[0, 1, 2].map((item) => (
                                <Skeleton
                                    key={item}
                                    height={isCompact ? 52 : 88}
                                />
                            ))}
                        </Stack>
                    )}

                    {!isLoading &&
                        versions.map((version) => {
                            const isCurrent = loadedVersionId === version.id;

                            return (
                                <LLMClassifierVersionHistoryItem
                                    key={version.id}
                                    ref={
                                        isCurrent ? loadedVersionRef : undefined
                                    }
                                    version={version}
                                    isCurrent={isCurrent}
                                    isCompact={isCompact}
                                    isPublished={isLLMClassifierVersionPublished(
                                        store.classifier.isPublished,
                                        version,
                                    )}
                                    onSelect={() => selectVersion(version.id)}
                                />
                            );
                        })}

                    {!isLoading && versions.length === 0 && (
                        <Typography color="gray.500" p={2} variant="body2">
                            Version history will appear after this model is
                            saved.
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/* Selecting a version from the list is a separate change. */}
            <Box
                ref={setInlineScroller}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Grows so the bar sits at the bottom even when the diff is short. */}
                <Box flex="1 0 auto">
                    <LLMClassifierVersionComparison onExpand={openFullScreen} />
                </Box>
                {approvalWorkflow && (
                    <Box
                        sx={(theme) => ({
                            position: "sticky",
                            bottom: 0,
                            pt: 2,
                            // Beats the z-index 2 that react-diff-viewer puts on
                            // its own sticky column headers.
                            zIndex: 10,
                            // Fades the diff out under the bar instead of letting rows run into it.
                            background: `linear-gradient(to top, ${theme.palette.background.default} 70%, transparent)`,
                        })}
                    >
                        {approvalWorkflow}
                    </Box>
                )}
            </Box>

            <VersionComparisonDialog
                ref={setFullScreenScroller}
                isOpen={isFullScreen}
                modelName={store.classifier.name || "Model"}
                onClose={() => setIsFullScreen(false)}
                footer={approvalWorkflow}
            />
        </Stack>
    );
});
